import {
  buildSidebarMenuSections,
  type MenuPermission as Permission,
  type SidebarMenuItem as MenuItem,
  type SidebarSubItem as SubItem,
} from '../config/sidebarMenu.tsx';

export type { MenuItem, Permission, SubItem };
export type UserRole = string;
export type UserType = 'user' | 'master';

const MENU_PERMISSIONS_STORAGE_KEY = 'menu_permissions';
const MENU_PERMISSIONS_MODE_STORAGE_KEY = 'menu_permissions_mode';
const CONTACT_FUNNEL_STAGES_STORAGE_KEY = 'contact_funnel_stages';
type MenuPermissionsMode = 'legacy' | 'granular';

function normalizePermissions(rawPermissions: unknown): Permission[] {
  if (!Array.isArray(rawPermissions)) {
    return [];
  }

  const seen = new Set<string>();
  return rawPermissions
    .map(permission => String(permission).trim().toLowerCase())
    .filter(permission => {
      if (!permission || seen.has(permission)) {
        return false;
      }
      seen.add(permission);
      return true;
    });
}

function readStoredPermissions(): Permission[] {
  const rawPermissions = localStorage.getItem(MENU_PERMISSIONS_STORAGE_KEY);
  if (!rawPermissions) {
    return [];
  }

  try {
    return normalizePermissions(JSON.parse(rawPermissions));
  } catch (error) {
    console.error('Erro ao ler permissões de menu:', error);
    return [];
  }
}

function readStoredMenuPermissionsMode(): MenuPermissionsMode {
  return localStorage.getItem(MENU_PERMISSIONS_MODE_STORAGE_KEY) === 'granular'
    ? 'granular'
    : 'legacy';
}

export const setUserPermissions = (
  menuPermissions: Permission[],
  contactFunnelStages?: string[],
  menuPermissionsMode: MenuPermissionsMode = 'legacy',
): void => {
  localStorage.setItem(
    MENU_PERMISSIONS_STORAGE_KEY,
    JSON.stringify(normalizePermissions(menuPermissions)),
  );
  localStorage.setItem(MENU_PERMISSIONS_MODE_STORAGE_KEY, menuPermissionsMode);

  if (contactFunnelStages) {
    localStorage.setItem(
      CONTACT_FUNNEL_STAGES_STORAGE_KEY,
      JSON.stringify(normalizePermissions(contactFunnelStages)),
    );
  }

  window.dispatchEvent(new CustomEvent('userPermissionsChanged'));
};

export const clearUserPermissions = (): void => {
  localStorage.removeItem(MENU_PERMISSIONS_STORAGE_KEY);
  localStorage.removeItem(MENU_PERMISSIONS_MODE_STORAGE_KEY);
  localStorage.removeItem(CONTACT_FUNNEL_STAGES_STORAGE_KEY);
  window.dispatchEvent(new CustomEvent('userPermissionsChanged'));
};

export const getUserRole = (): UserRole | null => {
  if (getUserType() === 'master') {
    return 'MASTER';
  }

  const userTeam = localStorage.getItem('user_team');
  if (userTeam) {
    return userTeam;
  }

  const userStr = localStorage.getItem('user');
  if (!userStr) {
    return null;
  }

  try {
    const user = JSON.parse(userStr);
    return user.team?.code || null;
  } catch (error) {
    console.error('Erro ao parsear user:', error);
    return null;
  }
};

export const getUserType = (): UserType => {
  const userType = localStorage.getItem('user_type');
  return userType === 'master' ? 'master' : 'user';
};

export const isUserMaster = (): boolean => {
  return getUserType() === 'master';
};

export const hasPermission = (permission: Permission): boolean => {
  if (isUserMaster()) {
    return true;
  }

  const permissions = getUserPermissions();
  if (permission === 'mcp') {
    return permissions.includes('mcp');
  }
  if (permissions.includes('*') || permissions.includes(permission)) {
    return true;
  }

  if (readStoredMenuPermissionsMode() !== 'legacy' || !permission.includes('.')) {
    return false;
  }

  const parentPermission = permission.split('.')[0];
  return permissions.includes(parentPermission);
};

export const hasPermissionGroup = (permission: Permission): boolean => {
  if (hasPermission(permission)) {
    return true;
  }

  const permissionPrefix = `${permission}.`;
  return getUserPermissions().some(userPermission => userPermission.startsWith(permissionPrefix));
};

export const filterMenusByPermissions = (menuItems: MenuItem[]): MenuItem[] => {
  const isMaster = isUserMaster();
  const canAccessMenuNode = (item: {
    permission?: Permission;
    permissionAliases?: Permission[];
    masterOnly?: boolean;
  }): boolean => {
    if (item.masterOnly && !isMaster) {
      return false;
    }

    if (!item.permission && !item.permissionAliases?.length) {
      return true;
    }

    return Boolean(
      (item.permission && hasPermission(item.permission)) ||
      item.permissionAliases?.some(alias => hasPermission(alias)),
    );
  };

  return menuItems.reduce<MenuItem[]>((filteredItems, item) => {
    const filteredSubItems = item.subItems?.filter(canAccessMenuNode) || [];
    const canAccessItem = canAccessMenuNode(item);

    if (!canAccessItem && filteredSubItems.length === 0) {
      return filteredItems;
    }

    filteredItems.push({
      ...item,
      path: canAccessItem ? item.path : filteredSubItems[0].path,
      subItems: item.subItems ? filteredSubItems : undefined,
    });

    return filteredItems;
  }, []);
};

export const getDefaultAuthorizedPath = (fallbackPath = '/dashboard'): string => {
  const firstAllowedMenuItem = buildSidebarMenuSections()
    .flatMap(section => filterMenusByPermissions(section.items))
    .find(item => Boolean(item.path));

  return firstAllowedMenuItem?.path || fallbackPath;
};

export const getUserPermissions = (): Permission[] => {
  if (isUserMaster()) {
    return ['*'];
  }

  return readStoredPermissions();
};

export const canAccessAdvancedSettings = (): boolean => {
  return hasPermissionGroup('prompt') || hasPermissionGroup('clinic');
};

export const debugUserInfo = () => {
  return {
    userRole: getUserRole(),
    userType: getUserType(),
    permissions: getUserPermissions(),
    isRawData: {
      user_type: localStorage.getItem('user_type'),
      user_team: localStorage.getItem('user_team'),
      user_id: localStorage.getItem('user_id'),
      menu_permissions: localStorage.getItem(MENU_PERMISSIONS_STORAGE_KEY),
      menu_permissions_mode: localStorage.getItem(MENU_PERMISSIONS_MODE_STORAGE_KEY),
      contact_funnel_stages: localStorage.getItem(CONTACT_FUNNEL_STAGES_STORAGE_KEY),
    },
  };
};
